import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Check } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { ADMIN_TRAIN } from "@/constants/testIds";

const EMPTY_MODULE = {
  track_key: "affiliate", number: 1, title: "", description: "", content: "",
  duration_minutes: 15, order: 1, status: "active",
  subcategories: [{ title: "", body: "" }],
  questions: [],
};

export default function AdminTrainingModules() {
  const [tracks, setTracks] = useState([]);
  const [modules, setModules] = useState([]);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);  // module being edited or "new"
  const [form, setForm] = useState(EMPTY_MODULE);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const [t, m] = await Promise.all([
        api.get("/admin/training/tracks"),
        api.get(filter ? `/admin/training/modules?track_key=${filter}` : "/admin/training/modules"),
      ]);
      setTracks(t.data); setModules(m.data);
    } catch (e) { setErr(apiErrorMessage(e)); }
  };
  useEffect(() => { reload(); /* eslint-disable-line */ }, [filter]);

  const startNew = () => { setEditing("new"); setForm(EMPTY_MODULE); };
  const startEdit = (m) => {
    setEditing(m.id);
    setForm({
      track_key: m.track_key, number: m.number, title: m.title, description: m.description,
      content: m.content, duration_minutes: m.duration_minutes, order: m.order, status: m.status,
      subcategories: (m.subcategories || []).map((s) => ({ id: s.id, title: s.title, body: s.body })),
      questions: m.questions || [],
    });
  };
  const cancel = () => { setEditing(null); setForm(EMPTY_MODULE); setErr(""); };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      if (editing === "new") await api.post("/admin/training/modules", form);
      else await api.put(`/admin/training/modules/${editing}`, form);
      cancel(); await reload();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };
  const removeModule = async (id) => {
    if (!window.confirm("Soft-delete this module?")) return;
    await api.delete(`/admin/training/modules/${id}`);
    await reload();
  };

  return (
    <div className="space-y-6">
      <header className="fade-up">
        <div className="eyebrow">Training admin</div>
        <h1 className="headline-xl">Modules & tracks</h1>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <select className="bg-lifted-surface border border-white/10 rounded-md px-3 py-2 text-sm" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">All tracks</option>
          {tracks.map((t) => <option key={t.key} value={t.key}>{t.title}</option>)}
        </select>
        <button data-testid={ADMIN_TRAIN.newModuleBtn} onClick={startNew} className="btn-lifted !py-2 !px-4 !text-xs">
          <Plus className="h-3 w-3" /> New module
        </button>
      </div>

      {err && <div className="glass-panel p-3 text-sm text-rose-300 border-rose-500/30">{err}</div>}

      {editing && <ModuleEditor form={form} setForm={setForm} tracks={tracks} onSave={save} onCancel={cancel} busy={busy} isNew={editing === "new"} />}

      <div data-testid={ADMIN_TRAIN.modulesList} className="glass-panel overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[0.7rem] tracking-[0.22em] uppercase text-lifted-mute font-bebas border-b border-white/5">
              <th className="px-4 py-3">Track</th>
              <th className="px-4 py-3">#</th>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Subs</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {modules.map((m) => (
              <tr key={m.id} className="border-b border-white/5">
                <td className="px-4 py-3"><span className="chip">{m.track_key}</span></td>
                <td className="px-4 py-3 font-anton text-lifted-green">{m.number}</td>
                <td className="px-4 py-3 text-lifted-text">{m.title}</td>
                <td className="px-4 py-3 text-lifted-dim">{m.subcategories?.length || 0}</td>
                <td className="px-4 py-3 text-xs uppercase font-bebas tracking-[0.18em]">{m.status}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-2">
                    <button data-testid={ADMIN_TRAIN.moduleEdit(m.id)} onClick={() => startEdit(m)} className="btn-ghost !py-1.5 !px-3 !text-[0.65rem]"><Pencil className="h-3 w-3" /> Edit</button>
                    <button data-testid={ADMIN_TRAIN.moduleDelete(m.id)} onClick={() => removeModule(m.id)} className="btn-ghost !py-1.5 !px-3 !text-[0.65rem] hover:!text-rose-300"><Trash2 className="h-3 w-3" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {modules.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-lifted-mute">No modules yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ModuleEditor({ form, setForm, tracks, onSave, onCancel, busy, isNew }) {
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "number" ? parseInt(e.target.value, 10) || 0 : e.target.value }));
  const setSub = (i, k, v) => setForm((f) => ({ ...f, subcategories: f.subcategories.map((s, j) => j === i ? { ...s, [k]: v } : s) }));
  const addSub = () => setForm((f) => ({ ...f, subcategories: [...f.subcategories, { title: "", body: "" }] }));
  const rmSub = (i) => setForm((f) => ({ ...f, subcategories: f.subcategories.filter((_, j) => j !== i) }));

  return (
    <div className="glass-panel p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="font-anton text-2xl uppercase">{isNew ? "New module" : "Edit module"}</div>
        <button onClick={onCancel} className="btn-ghost !py-1.5 !px-3 !text-[0.7rem]"><X className="h-3 w-3" /> Cancel</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div><label className="lifted-label">Track</label>
          <select className="input-lifted" value={form.track_key} onChange={upd("track_key")}>
            {tracks.map((t) => <option key={t.key} value={t.key}>{t.title}</option>)}
          </select>
        </div>
        <div><label className="lifted-label">Number</label><input type="number" min={1} className="input-lifted" value={form.number} onChange={upd("number")} /></div>
        <div><label className="lifted-label">Duration (min)</label><input type="number" className="input-lifted" value={form.duration_minutes} onChange={upd("duration_minutes")} /></div>
        <div><label className="lifted-label">Status</label>
          <select className="input-lifted" value={form.status} onChange={upd("status")}>
            <option value="active">active</option><option value="draft">draft</option><option value="deleted">deleted</option>
          </select>
        </div>
      </div>
      <div><label className="lifted-label">Title</label><input className="input-lifted" value={form.title} onChange={upd("title")} /></div>
      <div><label className="lifted-label">Description</label><input className="input-lifted" value={form.description} onChange={upd("description")} /></div>
      <div><label className="lifted-label">Content (markdown / plain text)</label><textarea className="input-lifted min-h-[140px]" value={form.content} onChange={upd("content")} /></div>

      <div>
        <div className="flex items-center justify-between">
          <label className="lifted-label">Sub-modules</label>
          <button onClick={addSub} className="btn-ghost !py-1.5 !px-3 !text-[0.65rem]"><Plus className="h-3 w-3" /> Add</button>
        </div>
        <ul className="space-y-2">
          {form.subcategories.map((s, i) => (
            <li key={i} className="flex gap-2">
              <input className="input-lifted" placeholder="Title" value={s.title} onChange={(e) => setSub(i, "title", e.target.value)} />
              <button onClick={() => rmSub(i)} className="btn-ghost !py-1.5 !px-3 !text-[0.65rem]"><Trash2 className="h-3 w-3" /></button>
            </li>
          ))}
        </ul>
      </div>

      <div className="pt-2 flex gap-3">
        <button data-testid={ADMIN_TRAIN.newModuleSave} onClick={onSave} disabled={busy} className="btn-lifted"><Check className="h-3 w-3" /> {busy ? "Saving…" : "Save"}</button>
        <button onClick={onCancel} className="btn-ghost"><X className="h-3 w-3" /> Cancel</button>
      </div>
    </div>
  );
}
